// The page is never left blank. A screen that throws, or a chunk an older release named and the
// server no longer has (a deploy since this page opened), would make React drop the whole tree.
// A missing chunk reloads the page once to fetch the new release; anything else shows a board
// with a reload button and goes to the error reports.
import { Component, type ReactNode } from 'react';
import { reportCaught } from '../lib/errors';
import { S } from '../strings';
import { Logo } from './Logo';

const RELOADED = 'wahala:reloaded';
/** One automatic reload per this long: a release still missing the chunk gets the board. */
const RELOAD_GAP_MS = 60_000;

const CHUNK =
  /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError/i;

export function isChunkError(error: unknown): boolean {
  return CHUNK.test(error instanceof Error ? `${error.name} ${error.message}` : String(error));
}

/** Reloads the page unless it reloaded for this a short while ago. True when it reloads. */
export function reloadOnce(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED) ?? 0);
    if (now - last < RELOAD_GAP_MS) return false;
    sessionStorage.setItem(RELOADED, String(now));
  } catch {
    // no storage in this frame: reloading without a guard could loop, so show the board
    return false;
  }
  location.reload();
  return true;
}

export class Crash extends Component<{ children: ReactNode }, { broken: boolean }> {
  override state = { broken: false };

  static getDerivedStateFromError(): { broken: boolean } {
    return { broken: true };
  }

  override componentDidCatch(error: unknown): void {
    if (isChunkError(error) && reloadOnce()) return;
    reportCaught(error);
  }

  override render(): ReactNode {
    if (!this.state.broken) return this.props.children;
    return (
      <main className="screen center">
        <Logo />
        <section className="card">
          <h2 className="display small">{S.broken.title}</h2>
          <p>{S.broken.body}</p>
          <button
            className="wh-btn wh-btn--carry wh-btn--md wh-btn--block"
            type="button"
            onClick={() => location.reload()}
          >
            {S.broken.reload}
          </button>
        </section>
      </main>
    );
  }
}
